import type {
  AgentCreated,
  BetaRequest,
  BetaRequestList,
  BetaRequestStatus,
  AgentView,
  SudoPolicyResult,
  McpConfigView,
  McpConfigResult,
  McpDesiredServer,
  AgentsList,
  AuditList,
  AuditQuery,
  DirListReply,
  ChatChannel,
  ChatMember,
  ChatMessage,
  EnrollTokenCreated,
  EnrollTokenList,
  GlobalTunnelList,
  TunnelList,
  TunnelOpenReply,
  ErrorEnvelope,
  LoginResponse,
  NxEndpointCreated,
  NxEndpointView,
  NxEndpointsList,
  OperatorAdminView,
  OperatorTokenCreated,
  OperatorTokenList,
  OperatorView,
  OperatorsList,
  Organization,
  OrganizationsList,
  PublicConfig,
  ReleaseSigningKey,
  ReleaseSigningKeyCreated,
  ReleaseSigningKeysList,
  ReleaseView,
  ReleasesList,
  RemoteSession,
  RemoteSessionsList,
  SessionEngine,
  UpdateChannel,
  UpdatePolicy,
} from "./types";

// Global 401 handler — set once at boot (main.ts). When a request to a
// non-auth endpoint comes back 401, the session is gone (expired/revoked);
// we reset auth state and bounce to /login instead of letting each view
// render a bare "unauthorized" error. Auth endpoints (/api/me, /api/auth/*)
// 401 legitimately and are excluded so login/probe aren't caught in a loop.
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail?: unknown,
  ) {
    super(`${code} (${status})`);
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let envelope: ErrorEnvelope | undefined;
    try {
      envelope = (await res.json()) as ErrorEnvelope;
    } catch {
      // body was not JSON
    }
    throw new ApiError(res.status, envelope?.error.code ?? "http.error", envelope?.error.details);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

// Read the double-submit CSRF token written by `/api/auth/login` into the
// `nyx_csrf` cookie. Mutating requests must echo it back as `X-CSRF-Token`
// per §14.1.
function readCsrfCookie(): string | null {
  const target = "nyx_csrf=";
  for (const part of document.cookie.split(";")) {
    const t = part.trim();
    if (t.startsWith(target)) return t.slice(target.length);
  }
  return null;
}

function csrfHeaders(method: string): HeadersInit {
  const mutating = method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
  if (!mutating) return {};
  const token = readCsrfCookie();
  return token ? { "X-CSRF-Token": token } : {};
}

async function request<T>(path: string, init: RequestInit = {}, expectJsonBody = true): Promise<T> {
  const method = init.method ?? "GET";
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...csrfHeaders(method),
    ...(init.headers ?? {}),
  };
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    method,
    headers,
  });
  // Session lost mid-app → reset auth + redirect (see unauthorizedHandler).
  // Skip auth endpoints, which 401 as part of normal login/probe flow.
  if (res.status === 401 && path !== "/api/me" && !path.startsWith("/api/auth/")) {
    unauthorizedHandler?.();
  }
  if (!expectJsonBody && res.ok) return undefined as T;
  return parseJson<T>(res);
}

export const api = {
  config(): Promise<PublicConfig> {
    return request<PublicConfig>("/api/config");
  },

  login(email: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  logout(): Promise<void> {
    return request<void>("/api/auth/logout", { method: "POST" }, false);
  },

  me(signal?: AbortSignal): Promise<OperatorView> {
    return request<OperatorView>("/api/me", { signal, cache: "no-store" });
  },

  tokens: {
    list(): Promise<OperatorTokenList> {
      return request<OperatorTokenList>("/api/me/tokens");
    },
    create(name: string): Promise<OperatorTokenCreated> {
      return request<OperatorTokenCreated>("/api/me/tokens", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
    },
    revoke(id: string): Promise<void> {
      return request<void>(`/api/me/tokens/${id}`, { method: "DELETE" }, false);
    },
  },

  consent: {
    redeem(code: string): Promise<{ session_id: string }> {
      return request<{ session_id: string }>("/api/consent/redeem", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
    },
  },

  admin: {
    betaRequests: {
      list(): Promise<BetaRequestList> {
        return request<BetaRequestList>("/api/admin/beta-requests");
      },
      update(id: string, status: BetaRequestStatus): Promise<BetaRequest> {
        return request<BetaRequest>(`/api/admin/beta-requests/${id}`, {
          method: "PATCH",
          body: JSON.stringify({ status }),
        });
      },
    },
    organizations: {
      list(): Promise<OrganizationsList> {
        return request<OrganizationsList>("/api/admin/organizations");
      },
      create(body: { slug: string; name: string }): Promise<Organization> {
        return request<Organization>("/api/admin/organizations", {
          method: "POST",
          body: JSON.stringify(body),
        });
      },
      update(id: string, body: { slug?: string; name?: string }): Promise<Organization> {
        return request<Organization>(`/api/admin/organizations/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      },
      delete(id: string): Promise<void> {
        return request<void>(`/api/admin/organizations/${id}`, { method: "DELETE" }, false);
      },
    },
    operators: {
      list(): Promise<OperatorsList> {
        return request<OperatorsList>("/api/admin/operators");
      },
      create(body: {
        email: string;
        password: string;
        role: string;
        organization_id?: string;
      }): Promise<OperatorAdminView> {
        return request<OperatorAdminView>("/api/admin/operators", {
          method: "POST",
          body: JSON.stringify(body),
        });
      },
      update(
        id: string,
        body: {
          email?: string;
          password?: string;
          role?: string;
          status?: "enabled" | "disabled";
          terminal_login?: string;
          approval_scopes?: string[];
        },
      ): Promise<OperatorAdminView> {
        return request<OperatorAdminView>(`/api/admin/operators/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      },
      delete(id: string): Promise<void> {
        return request<void>(`/api/admin/operators/${id}`, { method: "DELETE" }, false);
      },
    },
    audit: {
      list(query: AuditQuery = {}): Promise<AuditList> {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
          if (v !== undefined && v !== null && `${v}` !== "") {
            params.set(k, String(v));
          }
        }
        const q = params.toString();
        return request<AuditList>(`/api/admin/audit${q ? `?${q}` : ""}`);
      },
    },
  },

  agents: {
    list(): Promise<AgentsList> {
      return request<AgentsList>("/api/agents");
    },
    create(body: {
      login: string;
      hostname: string;
      display_name?: string;
      organization_id?: string;
      access_mode?: "managed" | "consent-only";
    }): Promise<AgentCreated> {
      return request<AgentCreated>("/api/agents", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    update(
      id: string,
      body: {
        hostname?: string;
        display_name?: string;
        status?: "enabled" | "disabled";
        update_channel?: UpdateChannel;
        update_policy?: UpdatePolicy;
        relay_enabled?: boolean;
        access_mode?: "managed" | "consent-only";
        tags?: string[];
      },
    ): Promise<AgentView> {
      return request<AgentView>(`/api/agents/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },
    delete(id: string): Promise<void> {
      return request<void>(`/api/agents/${id}`, { method: "DELETE" }, false);
    },
    dirList(id: string, path?: string): Promise<DirListReply> {
      const q = path ? `?path=${encodeURIComponent(path)}` : "";
      return request<DirListReply>(`/api/agents/${id}/dir-list${q}`);
    },
    // Set the worker sudo policy for a machine (non-root agent, JIT root).
    // `all` requires ttl_secs (passwordless root must expire); `list` needs
    // commands (ttl optional); `none` revokes.
    setSudoPolicy(
      id: string,
      body: { policy: "none" | "all" | "list"; commands?: string[]; ttl_secs?: number },
    ): Promise<SudoPolicyResult> {
      return request<SudoPolicyResult>(`/api/agents/${id}/sudo-policy`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    // MCP skill config (§skills-via-MCP S3c): read the machine's desired + catalog
    // + running skill servers, and set which skills are enabled (name + params).
    // The CP pushes only names/params; the agent maps them via its local catalog.
    getMcpConfig(id: string): Promise<McpConfigView> {
      return request<McpConfigView>(`/api/agents/${id}/mcp-config`);
    },
    setMcpConfig(id: string, servers: McpDesiredServer[]): Promise<McpConfigResult> {
      return request<McpConfigResult>(`/api/agents/${id}/mcp-config`, {
        method: "POST",
        body: JSON.stringify({ servers }),
      });
    },
    // Agent enrollment (join) tokens — admin-only.
    enrollTokens: {
      list(): Promise<EnrollTokenList> {
        return request<EnrollTokenList>("/api/admin/agents/enroll-tokens");
      },
      create(body: {
        max_uses?: number;
        ttl_secs?: number;
        ip_allowlist?: string[];
        hostname_pattern?: string;
        relay_enabled?: boolean;
        access_mode?: "managed" | "consent-only";
        organization_id?: string;
        tags?: string[];
      }): Promise<EnrollTokenCreated> {
        return request<EnrollTokenCreated>("/api/admin/agents/enroll-tokens", {
          method: "POST",
          body: JSON.stringify(body),
        });
      },
      revoke(id: string): Promise<void> {
        return request<void>(`/api/admin/agents/enroll-tokens/${id}`, { method: "DELETE" }, false);
      },
    },
    // Dashboard-managed relay tunnels hosted on agent A (forward to agent B).
    tunnels: {
      list(agentId: string): Promise<TunnelList> {
        return request<TunnelList>(`/api/agents/${agentId}/tunnels`);
      },
      create(
        agentId: string,
        body: { target_agent_id: string; remote_port?: number; listen_addr?: string },
      ): Promise<TunnelOpenReply> {
        return request<TunnelOpenReply>(`/api/agents/${agentId}/tunnels`, {
          method: "POST",
          body: JSON.stringify(body),
        });
      },
      stop(agentId: string, tunnelId: string): Promise<void> {
        return request<void>(
          `/api/agents/${agentId}/tunnels/${tunnelId}`,
          { method: "DELETE" },
          false,
        );
      },
    },
  },

  // Chat (AI-worker layer): channels + messages + approvals.
  chat: {
    listChannels(): Promise<{ channels: ChatChannel[] }> {
      return request<{ channels: ChatChannel[] }>("/api/channels");
    },
    createChannel(body: {
      name: string;
      description?: string;
      worker_preset?: string;
    }): Promise<ChatChannel> {
      return request<ChatChannel>("/api/channels", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    listMessages(channelId: string, limit?: number): Promise<{ messages: ChatMessage[] }> {
      const q = limit ? `?limit=${limit}` : "";
      return request<{ messages: ChatMessage[] }>(`/api/channels/${channelId}/messages${q}`);
    },
    postMessage(channelId: string, content: string): Promise<ChatMessage> {
      return request<ChatMessage>(`/api/channels/${channelId}/messages`, {
        method: "POST",
        body: JSON.stringify({ content }),
      });
    },
    approve(
      channelId: string,
      requestMessageId: string,
      decision: "approved" | "rejected",
    ): Promise<ChatMessage> {
      return request<ChatMessage>(`/api/channels/${channelId}/approvals`, {
        method: "POST",
        body: JSON.stringify({ request_message_id: requestMessageId, decision }),
      });
    },
    listMembers(channelId: string): Promise<{ members: ChatMember[] }> {
      return request<{ members: ChatMember[] }>(`/api/channels/${channelId}/members`);
    },
    addMember(channelId: string, workerId: string): Promise<void> {
      return request<void>(
        `/api/channels/${channelId}/members`,
        { method: "POST", body: JSON.stringify({ worker_id: workerId }) },
        false,
      );
    },
    removeMember(channelId: string, workerId: string): Promise<void> {
      return request<void>(
        `/api/channels/${channelId}/members/${workerId}`,
        { method: "DELETE" },
        false,
      );
    },
    workerControl(
      channelId: string,
      workerId: string,
      action: "stop" | "restart" | "pause" | "resume",
    ): Promise<{ ok: boolean }> {
      return request<{ ok: boolean }>(`/api/channels/${channelId}/workers/${workerId}/control`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
    },
    listWorkers(): Promise<{ workers: ChatMember[] }> {
      return request<{ workers: ChatMember[] }>("/api/workers");
    },
    workerLifecycle(workerId: string, desired: "running" | "stopped"): Promise<ChatMember> {
      return request<ChatMember>(`/api/workers/${workerId}/lifecycle`, {
        method: "POST",
        body: JSON.stringify({ desired }),
      });
    },
    createWorker(body: {
      name: string;
      preset?: string;
      zone?: string;
      memory_scope?: string;
    }): Promise<ChatMember> {
      return request<ChatMember>("/api/workers", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    deleteWorker(workerId: string): Promise<void> {
      return request<void>(`/api/workers/${workerId}`, { method: "DELETE" }, false);
    },
  },

  // Global view of all managed tunnels (per-agent create/stop live under
  // api.agents.tunnels). Backed by GET /api/tunnels which fans out to agents.
  tunnels: {
    listAll(): Promise<GlobalTunnelList> {
      return request<GlobalTunnelList>("/api/tunnels");
    },
  },

  sessions: {
    uploadImage(
      sessionId: string,
      body: { mime_type: string; data_b64: string },
      signal?: AbortSignal,
    ): Promise<{ path: string }> {
      return request(`/api/sessions/${encodeURIComponent(sessionId)}/images`, {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      });
    },
    listAll(): Promise<RemoteSessionsList> {
      return request<RemoteSessionsList>("/api/sessions");
    },
    listForAgent(agentId: string): Promise<RemoteSessionsList> {
      return request<RemoteSessionsList>(`/api/agents/${agentId}/sessions`);
    },
    start(
      agentId: string,
      body: {
        engine: SessionEngine;
        name?: string;
        working_dir?: string;
        cols?: number;
        rows?: number;
        relay_target?: string;
        relay_port?: number;
        ssh_user?: string;
      },
    ): Promise<RemoteSession> {
      return request<RemoteSession>(`/api/agents/${agentId}/sessions`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    stop(agentId: string, sessionId: string): Promise<void> {
      return request<void>(
        `/api/agents/${agentId}/sessions/${sessionId}`,
        { method: "DELETE" },
        false,
      );
    },
  },

  nx: {
    profileList(id: string, tool?: string): Promise<{ profiles: import("./types").NxProfile[] }> {
      const q = tool ? `?tool=${encodeURIComponent(tool)}` : "";
      return request(`/api/nx/${id}/profiles${q}`);
    },
    dirList(id: string, path?: string): Promise<DirListReply> {
      const q = path ? `?path=${encodeURIComponent(path)}` : "";
      return request<DirListReply>(`/api/nx/${id}/dir-list${q}`);
    },
    proxyList(id: string): Promise<{ proxies: import("./types").NxProxy[] }> {
      return request(`/api/nx/${id}/proxy`);
    },
    proxySet(id: string, body: { tool: string; profile: string; selector: string }): Promise<void> {
      return request<void>(
        `/api/nx/${id}/proxy`,
        { method: "PATCH", body: JSON.stringify(body) },
        false,
      );
    },
    threadList(
      id: string,
      profile: string,
      filter?: string,
    ): Promise<{ threads: import("./types").NxThread[] }> {
      const params = new URLSearchParams({ profile });
      if (filter) params.set("filter", filter);
      return request(`/api/nx/${id}/threads?${params.toString()}`);
    },
    threadMessages(
      id: string,
      threadId: string,
      profile: string,
      limit?: number,
    ): Promise<{ messages: import("./types").NxThreadMessage[] }> {
      const params = new URLSearchParams({ profile });
      if (limit != null) params.set("limit", String(limit));
      return request(`/api/nx/${id}/threads/${threadId}?${params.toString()}`);
    },
    startSession(
      id: string,
      body: {
        engine: SessionEngine;
        tool: "codex" | "claude";
        profile: string;
        proxy?: string;
        name?: string;
        working_dir?: string;
        cols?: number;
        rows?: number;
      },
    ): Promise<RemoteSession> {
      return request<RemoteSession>(`/api/nx/${id}/sessions`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    stopSession(id: string, sessionId: string): Promise<void> {
      return request<void>(`/api/nx/${id}/sessions/${sessionId}`, { method: "DELETE" }, false);
    },
  },

  nxEndpoints: {
    list(): Promise<NxEndpointsList> {
      return request<NxEndpointsList>("/api/nx-endpoints");
    },
    create(body: {
      login: string;
      hostname: string;
      display_name?: string;
      organization_id?: string;
    }): Promise<NxEndpointCreated> {
      return request<NxEndpointCreated>("/api/nx-endpoints", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    update(
      id: string,
      body: {
        hostname?: string;
        display_name?: string;
        status?: "enabled" | "disabled";
        update_channel?: UpdateChannel;
        update_policy?: UpdatePolicy;
      },
    ): Promise<NxEndpointView> {
      return request<NxEndpointView>(`/api/nx-endpoints/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },
    delete(id: string): Promise<void> {
      return request<void>(`/api/nx-endpoints/${id}`, { method: "DELETE" }, false);
    },
  },

  releases: {
    list(
      query: {
        product?: "agent" | "nx";
        channel?: UpdateChannel;
        os?: string;
        arch?: string;
        status?: string;
      } = {},
    ): Promise<ReleasesList> {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined) params.set(k, String(v));
      }
      const q = params.toString();
      return request<ReleasesList>(`/api/releases${q ? `?${q}` : ""}`);
    },
    promote(id: string, channel: UpdateChannel): Promise<ReleaseView> {
      return request<ReleaseView>(`/api/admin/releases/${id}/promote`, {
        method: "POST",
        body: JSON.stringify({ channel }),
      });
    },
    revoke(id: string): Promise<ReleaseView> {
      return request<ReleaseView>(`/api/admin/releases/${id}/revoke`, {
        method: "POST",
      });
    },
  },

  releaseKeys: {
    list(): Promise<ReleaseSigningKeysList> {
      return request<ReleaseSigningKeysList>("/api/admin/release-keys");
    },
    create(body: { name: string; public_key_hex?: string }): Promise<ReleaseSigningKeyCreated> {
      return request<ReleaseSigningKeyCreated>("/api/admin/release-keys", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    disable(id: string): Promise<ReleaseSigningKey> {
      return request<ReleaseSigningKey>(`/api/admin/release-keys/${id}`, {
        method: "DELETE",
      });
    },
  },
};
